import * as stylex from "@stylexjs/stylex";
import { estimateTextWidth } from "charts/lib/utils";
import { bisector, max } from "d3-array";
import * as scale from "d3-scale";
import { motion, transform } from "framer-motion";
import React from "react";
import ColorLegend from "../charts/lib/ColorLegend";
import { ChartTooltip, TooltipGridBodyWithLegend } from "../components/tooltip";
import { useContentRect, useMedia, usePointerPosition } from "../hooks";
import { fromNonEmptyArray, insertionOrderSet } from "../lib";
import { getContrastText, lighten } from "../lib/colorManipulator";
import * as M from "../materials";
import { breakpoints, colors } from "../materials/consts.stylex";
import { layout } from "../materials/layout.stylex";
import { typography } from "../materials/typography.stylex";
import { An, Ar, O, pipe } from "../prelude";
const MOAr = O.getMonoid(Ar.getMonoid());
// -----------------------------------------------------------------------------
// Helpers
function maxForStack(stack, acc) {
    return stack.reduce((sum, x)=>sum + acc(x), 0);
}
function maxAcrossStacks(stacks, acc) {
    return Object.keys(stacks).reduce((maxSum, stackKey)=>{
        const stackSum = maxForStack(stacks[stackKey], acc);
        return stackSum > maxSum ? stackSum : maxSum;
    }, 0);
}
function mkLayerScale(scale, getLayer, layerKeys) {
    return (stack, layerKey)=>{
        const upto = pipe(layerKeys, Ar.findIndex((x)=>x === layerKey), O.getOrElse(()=>0));
        return Ar.range(0, upto).map((i)=>layerKeys[i]).reduce((info, layerKey)=>{
            return pipe(stack, Ar.findFirst((x)=>getLayer(x) === layerKey), O.map((x)=>{
                return info.start === -1 ? {
                    start: 0,
                    height: scale(x)
                } : {
                    start: info.start + info.height,
                    height: scale(x)
                };
            }), O.getOrElse(()=>info));
        }, {
            start: -1,
            height: 0
        });
    };
}
// -----------------------------------------------------------------------------
// Components
const styles = {
    xLabel: {
        kDwRjp: "x13ylgtg",
        kcca2Q: "x1gr4c9z",
        kR6KzO: "xw6cgo0",
        $$css: true
    },
    xLabelRotate: {
        kR6KzO: "x1nibo1b",
        $$css: true
    },
    yLabel: {
        kDwRjp: "x13ylgtg",
        kcca2Q: "x1gr4c9z",
        kR6KzO: "x233ees",
        $$css: true
    },
    annotationLabel: {
        kDwRjp: "x13ylgtg",
        $$css: true
    }
};
export const StackedBar = React.memo(StackedBar_);
function StackedBar_(p) {
    const screenMDown = !useMedia(breakpoints.m);
    const [pointerPositionRef, pointerPosition] = usePointerPosition();
    const stackKeys = React.useMemo(()=>insertionOrderSet(p.data, p.getStack), [
        p.data,
        p.getStack
    ]);
    const layerKeys = React.useMemo(()=>insertionOrderSet(p.data, p.getLayer), [
        p.data,
        p.getLayer
    ]);
    const stacks = React.useMemo(()=>An.groupBy(p.getStack)(p.data), [
        p.data,
        p.getStack
    ]);
    const maxValue = React.useMemo(()=>maxAcrossStacks(stacks, p.getValue), [
        stacks,
        p.getValue
    ]);
    const marginBottom = React.useMemo(()=>{
        if (p.rotateXLabels) {
            const maxWidth = max(stackKeys, (d)=>estimateTextWidth(d, 12)) ?? 0;
            return Math.max(maxWidth + 20, 30);
        }
        return 30;
    }, [
        p.rotateXLabels,
        stackKeys
    ]);
    const haveAnnotations = !!p.annotations;
    const { margin, outerHeight, innerHeight, outerWidth, innerWidth } = React.useMemo(()=>{
        const margin = {
            top: haveAnnotations ? screenMDown ? 46 : 54 : 20,
            right: 0,
            bottom: marginBottom,
            left: p.hideYAxis ? 0 : screenMDown ? 30 : 50
        };
        return {
            margin,
            outerHeight: p.height + margin.top + margin.bottom,
            innerHeight: p.height,
            outerWidth: p.width,
            innerWidth: p.width - margin.left - margin.right
        };
    }, [
        screenMDown,
        p.height,
        p.width,
        p.hideYAxis,
        haveAnnotations,
        marginBottom
    ]);
    const xScale = React.useMemo(()=>scale.scaleBand().domain(fromNonEmptyArray(stackKeys)).range([
            0,
            innerWidth
        ]).paddingInner(1 / (innerWidth / stackKeys.length)).paddingOuter(0), [
        stackKeys,
        innerWidth
    ]);
    const xScaleInvert = React.useMemo(()=>{
        const steps = stackKeys.map((x)=>({
                domain: x,
                range: xScale(x)
            }));
        return (pos)=>{
            const idx = bisector((x)=>x.range).left(steps, pos);
            return (steps[idx - 1] || steps[0]).domain;
        };
    }, [
        stackKeys,
        xScale
    ]);
    const { yScale, yTicks } = React.useMemo(()=>{
        const s = scale.scaleLinear().domain([
            0,
            maxValue
        ]).range([
            0,
            innerHeight
        ]).nice(p.yNice);
        return {
            yScale: s,
            yTicks: s.ticks(6)
        };
    }, [
        maxValue,
        innerHeight,
        p.yNice
    ]);
    const layerScale = React.useMemo(()=>mkLayerScale((a)=>yScale(p.getValue(a)), (a)=>p.getLayer(a), layerKeys), [
        layerKeys,
        yScale,
        p.getLayer,
        p.getValue
    ]);
    const colorScale = React.useMemo(()=>scale.scaleOrdinal(p.colorPalette(layerKeys.length)).domain(layerKeys), [
        layerKeys,
        p.colorPalette
    ]);
    const annotations = React.useMemo(()=>{
        return p.annotations ? {
            label: p.annotations.label,
            items: p.annotations.items.map((x)=>({
                    x0: xScale(x.startStack),
                    x1: xScale(x.endStack),
                    label: p.getLayerLabel(x.layer),
                    color: colorScale(x.layer)
                }))
        } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        xScale,
        colorScale,
        p.getLayerLabel,
        p.annotations
    ]);
    const hoveredItem = pipe(pointerPosition, O.map((pos)=>xScaleInvert(pos.x)));
    const selectedItems = MOAr.concat(pipe(hoveredItem, O.map((x)=>An.of(x))), p.highlightedItems || O.none);
    const colorLegendValues = React.useMemo(()=>layerKeys.map((key)=>({
                color: colorScale(key),
                label: p.getLayerLabel(key)
            })), [
        layerKeys,
        colorScale,
        p
    ]);
    return <>
      <svg height={outerHeight} width={outerWidth}>
        {annotations && <g transform={`translate(${margin.left}, 0)`}>
            <StackAnnotation height={screenMDown ? 24 : 32} label={annotations.label} items={annotations.items}/>
          </g>}
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {stackKeys.map((key)=>{
        const stack = stacks[key];
        return <StackItem key={key} stack={stack} getLayer={p.getLayer} xScale={xScale} getStack={p.getStack} colorScale={colorScale} innerHeight={innerHeight} layerScale={layerScale} highlight={pipe(selectedItems, O.exists((xs)=>xs.includes(key)))} hasSelectedItems={O.isSome(selectedItems)}/>;
    })}
          {!p.hideXAxisLabel && <XAxisLabels stackKeys={stackKeys} xScale={xScale} formatX={p.formatX} innerHeight={innerHeight} rotate={p.rotateXLabels}/>}
          {!p.hideYAxis && <YAxisLongTicks yScale={yScale} yTicks={yTicks} innerHeight={innerHeight} innerWidth={innerWidth} yUnitLabel={p.yUnitLabel}/>}
        </g>

        <g transform={`translate(0, ${margin.top})`}>
          {!p.hideYAxis && <YAxisLabels yScale={yScale} yTicks={yTicks} width={margin.left - 4} innerHeight={innerHeight} formatY={p.formatY} formatYAxisLabel={p.formatYAxisLabel}/>}
        </g>

        {<g transform={`translate(${margin.left}, ${margin.top})`}>
            {stackKeys.filter((key)=>pipe(hoveredItem, O.exists((x)=>x === key))).map((key)=>{
        const stack = stacks[key];
        return <StackTooltip key={key} stack={stack} x={xScale(key)} width={xScale.bandwidth()} colorScale={colorScale} getLayer={p.getLayer} getLayerLabel={p.getLayerLabel} getValue={p.getValue} formatY={p.formatY} xScale={xScale} yScale={yScale} innerHeight={innerHeight} title={p.formatX(key, 0 /* Special case: we always want the rendering for the 0 position */ , stackKeys)}/>;
    })}

            {p.showTooltip && <rect ref={pointerPositionRef} x={0} y={0} width={innerWidth} height={innerHeight} fill="transparent"/>}
          </g>}
      </svg>
      <div>{!p.hideLegend && <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>}</div>
    </>;
}
export function StackedBarAuto(props) {
    const [ref, contentRect] = useContentRect();
    return <div ref={ref} {...stylex.props(layout.fullWidth)}>
      {contentRect.width > 0 && <StackedBar {...props} width={contentRect.width}/>}
    </div>;
}
// -----------------------------------------------------------------------------
const StackItem = React.memo(StackItem_);
function StackItem_({ stack, xScale, layerScale, getLayer, getStack, colorScale, innerHeight, highlight = false, hasSelectedItems }) {
    const xMax = xScale.range()[1];
    const mkDelay = transform([
        0,
        xMax
    ], [
        0,
        0.5
    ]);
    return <g>
      {stack.map((d)=>{
        const { start, height } = layerScale(stack, getLayer(d));
        const x = xScale(getStack(d));
        const y = innerHeight - (start + height);
        const delay = mkDelay(x || 0);
        const props = {
            x,
            y,
            height,
            fill: hasSelectedItems ? highlight ? colorScale(getLayer(d)) : lighten(colorScale(getLayer(d)), 0.5) : colorScale(getLayer(d)),
            opacity: 1
        };
        return <motion.rect key={`${getLayer(d)}`} width={xScale.bandwidth()} initial={false} animate={props} transition={{
            y: {
                delay
            },
            height: {
                delay
            }
        }}/>;
    })}
    </g>;
}
const XAxisLabels = React.memo(XAxisLabels_);
function XAxisLabels_({ xScale, stackKeys, formatX, innerHeight, rotate }) {
    return <>
      {stackKeys.map((stack, i)=>{
        const x = (xScale(stack) || 0) + xScale.bandwidth() / 2;
        return <g key={stack} transform={`translate(${x}, ${innerHeight})`}>
            <line className="xnpr05h x9zv0gs" y2={4}/>
            <text {...stylex.props(typography.meta, styles.xLabel, rotate && styles.xLabelRotate)} {...rotate ? {
            dx: "-1.2em",
            transform: "rotate(-90)"
        } : {
            dy: "1.2em"
        }}>
              {formatX(stack, i, stackKeys)}
            </text>
          </g>;
    })}
    </>;
}
const YAxisLongTicks = React.memo(YAxisLongTicks_);
function YAxisLongTicks_({ yScale, yTicks, innerHeight, innerWidth, yUnitLabel }) {
    return <>
      {yTicks.map((tick, i)=>{
        const isLast = i === yTicks.length - 1;
        const y = innerHeight - yScale(tick);
        return <React.Fragment key={tick}>
            <motion.line className="xnpr05h x9zv0gs" transition={{
            ease: "easeOut"
        }} x1={0} x2={innerWidth} initial={{
            y1: y,
            y2: y,
            opacity: 0
        }} animate={{
            y1: y,
            y2: y,
            opacity: [
                0,
                1
            ]
        }}/>
            {yUnitLabel && isLast && <motion.text {...stylex.props(typography.meta, styles.yLabel)} transition={{
            ease: "easeOut"
        }} x={0} dy={"-0.8em"} initial={{
            y
        }} animate={{
            y
        }}>
                {yUnitLabel}
              </motion.text>}
          </React.Fragment>;
    })}
    </>;
}
const YAxisLabels = React.memo(YAxisLabels_);
function YAxisLabels_({ yScale, yTicks, width, innerHeight, formatY, formatYAxisLabel }) {
    return <>
      {yTicks.map((tick)=>{
        const y = innerHeight - yScale(tick);
        return <React.Fragment key={tick}>
            <motion.line className="xnpr05h x1q4kd6z" transition={{
            ease: "easeOut"
        }} x1={0} x2={width} y1={y} y2={y} initial={{
            y1: y,
            y2: y,
            opacity: 0
        }} animate={{
            y1: y,
            y2: y,
            opacity: [
                0,
                1
            ]
        }}/>
            <motion.text {...stylex.props(typography.meta, styles.yLabel)} initial={{
            y,
            opacity: 0
        }} animate={{
            y,
            opacity: [
                0,
                1
            ]
        }} transition={{
            ease: "easeOut"
        }} dy={"-0.8em"}>
              {formatYAxisLabel ? formatYAxisLabel(tick) : formatY(tick)}
            </motion.text>
          </React.Fragment>;
    })}
    </>;
}
const StackTooltip = React.memo(StackTooltip_);
function StackTooltip_({ stack, colorScale, getLayer, getLayerLabel, getValue, formatY, yScale, innerHeight, title, x, width }) {
    const height = yScale(maxForStack(stack, getValue));
    const y = innerHeight - height;
    const rows = stack.map((x)=>[
            colorScale(getLayer(x)),
            getLayerLabel(getLayer(x)),
            formatY(getValue(x))
        ]).reverse();
    return <ChartTooltip title={title} content={<TooltipGridBodyWithLegend rows={rows}/>} placement="top" flipBehavior={[
        "top"
    ]} visible={true} hideOnClick={false}>
      <rect x={x} y={y} width={width} height={height} fill="transparent"/>
    </ChartTooltip>;
}
const StackAnnotation = React.memo(StackAnnotation_);
function StackAnnotation_({ label, items, height }) {
    const baseId = React.useId();
    const gradientId = `gradient-${baseId}`;
    return <>
      <defs>
        <linearGradient id={gradientId}>
          <stop offset="0%" stopColor="white"/>
          <stop offset="80%" stopColor="white"/>
          <stop offset="100%" stopColor="black"/>
        </linearGradient>
      </defs>
      <text x={items[0].x0} dy="0.8em" {...stylex.props(typography.meta, styles.annotationLabel)}>
        {label}
      </text>
      {items.map(({ label, color, x0, x1 }, i)=>{
        const maskId = `${baseId}-mask-${i}`;
        const width = x1 - x0;
        return <g key={i} transform={`translate(${x0},16)`}>
            <mask id={maskId}>
              <rect x="0" y="0" width={width - height / 4} height={height} fill={`url(#${gradientId})`}/>
            </mask>
            <path d={`M 0 0 L ${width - height / 2} 0 L ${width} ${height / 2} L ${width - height / 2} ${height} L 0 ${height} z`} fill={color}/>
            <text x={height / 8} y={height / 2} dy="0.3em" {...stylex.props(typography.meta)} style={{
            fill: getContrastText(color, {
                light: M.whiteText,
                dark: M.blackText
            })
        }} mask={`url(#${maskId})`}>
              {label}
            </text>
          </g>;
    })}
    </>;
}
